import React from "react";
import { Trans } from "@lingui/macro";
import { getColor } from "../../../../utils";
import { psidValidationEnv } from "../constants";
import { Alert } from "../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { typeDataProductInfoBulkActions } from "../index";
import {
  DotsVerticalIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import { selectTableData } from "../../../../../model/dashboardEdmProducts/selectors";
import { dEdmProductsSetDetailItemAC } from "../../../../../model/dashboardEdmProducts/dEdmProductsActionCreators";
import { ButtonLink } from "../../../../UI/ButtonLink/ButtonLink";

export type typeTableHeader = {
  variant?: string;
  bulkOperations: typeDataProductInfoBulkActions;
};

export const TableHeader: React.FC<typeTableHeader> = () => {
  return (
    <>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Product</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Category</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Count</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-left"
      >
        <Trans>Barcode</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3  text-xs font-medium uppercase tracking-wider text-left"
      >
        <Trans>Location</Trans>
      </th>
      <th scope="col" className="w-1" />
    </>
  );
};

export type typeAction = {
  title: React.JSX.Element;
  disableSwitcher?: boolean;
  action: (id: string) => void;
};

export type typeTableBody = {
  variant?: string;
  rowsActions: typeAction[];
  setOpenPopupDetails: React.Dispatch<React.SetStateAction<boolean>>;
};

const tdClassNames =
  "px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer";

export const TableBody: React.FC<typeTableBody> = ({
  variant,
  rowsActions,
  setOpenPopupDetails,
}) => {
  const dispatch = useDispatch();
  const tableData = useSelector(selectTableData);

  const color = getColor(variant);

  const onCellClickHandler = (id: string) => () => {
    setOpenPopupDetails(true);

    dispatch(dEdmProductsSetDetailItemAC(id));
  };

  let component;

  if (tableData.length) {
    component = tableData.map((item, index) => {
      const psid = psidValidationEnv ? (
        !item.psid || item.psid === "invalid" ? (
          <span className="text-red-400 flex gap-2">
            <ExclamationCircleIcon className="w-4 h-4" />
            <Trans>invalid PSID (need to check)</Trans>
          </span>
        ) : (
          item.psid
        )
      ) : !item.psid || item!.psid === "invalid" ? (
        "-"
      ) : (
        item.psid
      );

      return (
        <tr
          key={item.id}
          className={`${
            index % 2 === 0 ? "bg-white" : "bg-gray-50"
          } hover:bg-${color}-50`}
        >
          <td className={tdClassNames} onClick={onCellClickHandler(item.id)}>
            <div className="flex flex-col divide-y">
              <span className="font-medium text-black text-xs pb-1">
                {item.name || "-"}
              </span>
              <span className={"text-xs pt-1"}>
                <Trans>Brand</Trans>: {"-"}
              </span>
            </div>
          </td>
          <td className={tdClassNames} onClick={onCellClickHandler(item.id)}>
            <div className="flex flex-col divide-y">
              <span className="font-medium text-black text-xs pb-1">{"-"}</span>
              <span className={"text-xs pt-1"}>
                <Trans>PSID</Trans>: {psid ?? "-"}
              </span>
            </div>
          </td>
          <td className={tdClassNames} onClick={onCellClickHandler(item.id)}>
            <div className="flex flex-col divide-y">
              <span className="font-medium text-black text-xs pb-1">
                {item.amount ?? "-"}
              </span>
              <span className={"text-xs pt-1"}>
                <Trans>Units</Trans>: {item.units ?? "-"}
              </span>
            </div>
          </td>
          <td className={tdClassNames} onClick={onCellClickHandler(item.id)}>
            <div className="flex flex-col divide-y">
              <span className="font-medium text-black text-xs pb-1">
                {item.barcode || "-"}
              </span>
              <span className={"text-xs pt-1"}>
                {item.marked ? (
                  <Trans>Marked</Trans>
                ) : (
                  <Trans>Not marked</Trans>
                )}
              </span>
            </div>
          </td>
          <td
            className={`${tdClassNames} text-left`}
            onClick={onCellClickHandler(item.id)}
          >
            -
          </td>
          <td className="whitespace-nowrap text-sm text-gray-500 pr-2">
            <div className="relative flex">
              <div className="row-action-block">
                <DotsVerticalIcon className="row-action-list-open-button" />
                <div className="row-action-list">
                  <div className="flex gap-x-4">
                    {rowsActions.map((actionItem, ActionIndex) => {
                      return (
                        <ButtonLink
                          type="button"
                          key={ActionIndex}
                          label={actionItem.title}
                          onClick={() => actionItem.action(item.id)}
                        />
                      );
                      // if ("id" in actionItem) {
                      //   // @ts-ignore
                      //   switch (actionItem.id) {
                      //     case "delete":
                      //       if (!item.deleted) return link;
                      //       break;
                      //     case "recover":
                      //       if (item.deleted) return link;
                      //       break;
                      //     default:
                      //       return null;
                      //   }
                      // } else {
                      //   return item.deleted ? null : link;
                      // }
                    })}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      );
    });
  } else {
    component = (
      <tr>
        <td className="p-2" colSpan={7}>
          <Alert
            type="info"
            className="w-full"
            title={<Trans>The product list is empty.</Trans>}
          />
        </td>
      </tr>
    );
  }

  return <>{component}</>;
};
